import { shouldReloadForNewAdminBuild } from 'handlers/shouldReloadForNewAdminBuild'

/** Как часто проверять обновление SW и version.json */
const UPDATE_CHECK_INTERVAL_MS = 60_000

const KNOWN_BUILD_ID_KEY = 'admin-pwa-known-build-id'

type AdminVersionPayload = {
  buildId?: string
  builtAt?: string
}

/** Один промис на сессию — повторные вызовы не плодят listeners/intervals */
let registrationPromise: Promise<ServiceWorkerRegistration> | null = null
let updateHandlersAttached = false
let updateIntervalId: number | null = null

/**
 * Абсолютный URL service worker относительно origin.
 */
export const getAdminServiceWorkerUrl = (): string => {
  const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '')
  return `${window.location.origin}${publicUrl}/sw.js`
}

/**
 * Проверяет, что sw.js реально отдаётся как JS (не HTML от SPA fallback).
 * @param serviceWorkerUrl — абсолютный URL sw.js
 */
export const assertAdminServiceWorkerScript = async (
  serviceWorkerUrl: string
): Promise<void> => {
  let response: Response
  try {
    response = await fetch(serviceWorkerUrl, {
      cache: 'no-store',
      headers: {
        Accept: 'application/javascript, text/javascript, */*',
      },
    })
  } catch (error) {
    throw new Error(
      'Не удалось загрузить sw.js. Проверьте сеть и что админка открыта по HTTPS.'
    )
  }

  if (response.status === 404) {
    throw new Error(
      'Файл sw.js не найден на сервере (404). Пересоберите и задеплойте админку (npm run build).'
    )
  }

  if (!response.ok) {
    throw new Error(
      `Файл sw.js недоступен (HTTP ${response.status}). Проверьте деплой админки.`
    )
  }

  const contentType = response.headers.get('content-type') || ''
  if (contentType.includes('text/html')) {
    throw new Error(
      'Сервер отдаёт HTML вместо sw.js. В nginx исключите /sw.js из SPA fallback (try_files → index.html).'
    )
  }
}

/**
 * Регистрирует service worker и включает автообновление при деплое новой админки.
 * @returns registration
 */
export const registerAdminServiceWorker =
  async (): Promise<ServiceWorkerRegistration> => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      throw new Error('Service Worker не поддерживается в этом браузере')
    }

    if (!registrationPromise) {
      registrationPromise = registerAdminServiceWorkerInternal().catch(
        (error: unknown) => {
          registrationPromise = null
          throw error
        }
      )
    }

    return registrationPromise
  }

/**
 * Внутренняя регистрация SW с preflight-проверкой.
 */
const registerAdminServiceWorkerInternal =
  async (): Promise<ServiceWorkerRegistration> => {
    const serviceWorkerUrl = getAdminServiceWorkerUrl()
    await assertAdminServiceWorkerScript(serviceWorkerUrl)

    try {
      const registration = await navigator.serviceWorker.register(
        serviceWorkerUrl,
        {
          scope: '/',
          updateViaCache: 'none',
        }
      )

      setupServiceWorkerUpdateHandlers(registration)
      void checkRemoteAdminVersionAndReload()

      if (updateIntervalId == null) {
        updateIntervalId = window.setInterval(() => {
          void registration.update()
          void checkRemoteAdminVersionAndReload()
        }, UPDATE_CHECK_INTERVAL_MS)
      }

      return registration
    } catch (error) {
      console.error('Ошибка регистрации service worker:', error)
      const detail =
        error instanceof Error && error.message
          ? error.message
          : 'неизвестная ошибка'
      throw new Error(`Не удалось зарегистрировать service worker: ${detail}`)
    }
  }

/**
 * Когда найден новый SW — активируем и перезагружаем страницу.
 * @param registration — результат register()
 */
const setupServiceWorkerUpdateHandlers = (
  registration: ServiceWorkerRegistration
): void => {
  if (updateHandlersAttached) {
    return
  }
  updateHandlersAttached = true

  let isRefreshing = false

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (isRefreshing) {
      return
    }
    isRefreshing = true
    window.location.reload()
  })

  /**
   * Просит waiting-worker немедленно стать active.
   * @param worker — устанавливаемый SW
   */
  const activateWaitingWorker = (worker: ServiceWorker | null): void => {
    if (!worker) {
      return
    }
    worker.postMessage({ type: 'SKIP_WAITING' })
  }

  if (registration.waiting) {
    activateWaitingWorker(registration.waiting)
  }

  registration.addEventListener('updatefound', () => {
    const installingWorker = registration.installing
    if (!installingWorker) {
      return
    }
    installingWorker.addEventListener('statechange', () => {
      if (
        installingWorker.state === 'installed' &&
        navigator.serviceWorker.controller
      ) {
        activateWaitingWorker(installingWorker)
      }
    })
  })

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void registration.update()
      void checkRemoteAdminVersionAndReload()
    }
  })
}

/**
 * Сверяет /version.json с запомненным buildId; при отличии — reload.
 */
const checkRemoteAdminVersionAndReload = async (): Promise<void> => {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
    if (!response.ok) {
      return
    }
    const payload = (await response.json()) as AdminVersionPayload
    const remoteBuildId = payload?.buildId
    if (!remoteBuildId) {
      return
    }

    const knownBuildId = sessionStorage.getItem(KNOWN_BUILD_ID_KEY)
    if (!knownBuildId) {
      sessionStorage.setItem(KNOWN_BUILD_ID_KEY, remoteBuildId)
      return
    }

    if (shouldReloadForNewAdminBuild(knownBuildId, remoteBuildId)) {
      sessionStorage.setItem(KNOWN_BUILD_ID_KEY, remoteBuildId)
      window.location.reload()
    }
  } catch (_) {
    // version.json может отсутствовать в dev — не шумим
  }
}
